import { z } from 'zod'
import { ActivityId, HouseholdId, UserId } from './ids.ts'

// Activities are family-scoped — no `ownerId`. Anyone in the household can
// view, edit, and delete any activity.

// Canonical lists are exported so the UI's filter / picker chips and the
// schema never drift. Adding a value here makes it available everywhere.

export const ACTIVITY_TYPE = [
	'walk',
	'swim',
	'craft',
	'baking',
	'museum',
	'cinema',
	'playground',
	'cafe',
	'restaurant',
	'park',
	'shopping',
	'day_out',
	'picnic',
	'beach',
	'attraction',
	'holiday',
	'other',
] as const
export const ActivityType = z.enum(ACTIVITY_TYPE)
export type ActivityType = z.infer<typeof ActivityType>

// A place can carry several types (e.g. a park that's also a playground and a
// cafe). Stored rows created before multi-type carry a single nullable `type`;
// newer rows carry a `types` array. `migrateActivityTypes` (below) folds the
// legacy field into the array on read, and this schema drops any retired /
// unknown values so an old row still parses instead of failing the whole array.
const StoredTypes = z
	.preprocess(
		(val) =>
			Array.isArray(val)
				? val.filter((x) =>
						(ACTIVITY_TYPE as readonly unknown[]).includes(x),
					)
				: val,
		z.array(ActivityType),
	)
	.default([])

// `any` = no weather restriction. The others are positive matches: an
// activity tagged `rainy_ok` is a good shout when it's raining; `hot` is
// good on a hot day. Multiple may apply (e.g. a museum is both rainy_ok
// and hot).
export const WEATHER_SUIT = ['any', 'dry', 'rainy_ok', 'hot', 'cold'] as const
export const WeatherSuit = z.enum(WEATHER_SUIT)
export type WeatherSuit = z.infer<typeof WeatherSuit>

export const AUDIENCE = ['kids', 'family', 'date', 'solo'] as const
export const Audience = z.enum(AUDIENCE)
export type Audience = z.infer<typeof Audience>

// Age-suitability tags: who the place suits, youngest to oldest. `pram_friendly`
// flags easy buggy access (step-free-ish, gravel/grass ok); the `*_friendly`
// tags flag the age range it works for. Multiple may apply.
export const ACCESSIBILITY = [
	'pram_friendly',
	'baby_friendly',
	'toddler_friendly',
	'child_friendly',
] as const
export const Accessibility = z.enum(ACCESSIBILITY)
export type Accessibility = z.infer<typeof Accessibility>

// Stored rows may carry tags we've since retired (wheelchair, parking, …).
// Filter unknowns on read so an old activity still parses instead of failing
// the whole array. Client-supplied update bodies stay strict (below).
const StoredAccessibility = z
	.preprocess(
		(val) =>
			Array.isArray(val)
				? val.filter((x) =>
						(ACCESSIBILITY as readonly unknown[]).includes(x),
					)
				: val,
		z.array(Accessibility),
	)
	.default([])

// Whether the activity costs money. Mutually exclusive, so a single
// nullable value (like `type`) rather than an array — null until the
// user sets it.
export const COST = ['free', 'paid'] as const
export const Cost = z.enum(COST)
export type Cost = z.infer<typeof Cost>

// Whether the tags have been dealt with. `pending` until the user presses Done
// on the tag step; the Activities landing lists pending ones so a deferred
// tagging isn't lost. Named with the `Activity` prefix because shared/index.ts
// re-exports both this and recipe.ts, which has its own `TaggingStatus`.
export const ActivityTaggingStatus = z.enum(['pending', 'tagged'])
export type ActivityTaggingStatus = z.infer<typeof ActivityTaggingStatus>

export const ActivityName = z.string().trim().min(1).max(100)
export type ActivityName = z.infer<typeof ActivityName>

// The broader locality the place sits in (e.g. "Southsea"), captured from the
// geocoder at pick time. `.default(null)` for rows written before the field
// existed; a stored `''` collapses to null rather than failing `.min(1)` and
// taking the whole activity row down with it.
const StoredTown = z
	.preprocess(
		(val) =>
			typeof val === 'string' && val.trim().length === 0 ? null : val,
		z.string().trim().max(120).nullable(),
	)
	.default(null)

// Discriminated union: `home` activities have no coordinates; `place`
// activities pin a lat/lng with a human-readable name the user can edit
// (geocoder gives a starting suggestion).
export const ActivityLocation = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('home') }),
	z.object({
		kind: z.literal('place'),
		lat: z.number().gte(-90).lte(90),
		lng: z.number().gte(-180).lte(180),
		friendlyName: z.string().trim().min(1).max(120),
		town: StoredTown,
	}),
])
export type ActivityLocation = z.infer<typeof ActivityLocation>

// Fold a legacy single `type` (string or null) into the `types` array so rows
// written before multi-type still parse. Runs before the object schema; a row
// that already has `types` is left untouched.
const migrateActivityTypes = (val: unknown): unknown => {
	if (val === null || typeof val !== 'object' || Array.isArray(val))
		return val
	const o = val as Record<string, unknown>
	if (o.types !== undefined || !('type' in o)) return val
	const legacy = o.type
	const types =
		legacy == null ? [] : Array.isArray(legacy) ? legacy : [legacy]
	return { ...o, types }
}

// Set `taggingStatus` on rows written before the field existed: one already
// carrying a tag reads as tagged, one with none reads as pending. Runs after
// `migrateActivityTypes` so the legacy single `type` is already folded into
// `types`. Every field is read defensively — this sees the raw stored item.
const inferTaggingStatus = (val: unknown): unknown => {
	if (val === null || typeof val !== 'object' || Array.isArray(val))
		return val
	const o = val as Record<string, unknown>
	if (o.taggingStatus !== undefined) return val
	const filled = (x: unknown) => Array.isArray(x) && x.length > 0
	const tagged =
		filled(o.types) ||
		o.cost != null ||
		filled(o.weatherSuits) ||
		filled(o.audience) ||
		filled(o.accessibility)
	return { ...o, taggingStatus: tagged ? 'tagged' : 'pending' }
}

export const Activity = z.preprocess(
	(val) => inferTaggingStatus(migrateActivityTypes(val)),
	z.object({
		id: ActivityId,
		householdId: HouseholdId,
		name: ActivityName,
		location: ActivityLocation,
		// Empty until the user tags the place. May hold several types.
		types: StoredTypes,
		// null until set. `.default(null)` so rows created before this field
		// existed (no attribute) parse cleanly.
		cost: Cost.nullable().default(null),
		weatherSuits: z.array(WeatherSuit),
		audience: z.array(Audience),
		// Tolerant of missing attribute (old rows) and retired tag values.
		accessibility: StoredAccessibility,
		taggingStatus: ActivityTaggingStatus,
		notes: z.string().trim().max(2000).nullable(),
		// null until a user uploads a photo. Otherwise the canonical S3 URL
		// with a `?v=` cache-buster. The bucket is private: the client renders
		// it via the authed `GET /activities/{id}/image` proxy, never directly.
		photoUrl: z.url().nullable(),
		// ISO timestamps of when the family did this activity. Powers the
		// "haven't been in ages" sort. Empty until the first "Did this today".
		visits: z.array(z.iso.datetime()),
		createdAt: z.iso.datetime(),
		createdBy: UserId,
	}),
)
export type Activity = z.infer<typeof Activity>

export const ActivityListResponse = z.object({
	activities: z.array(Activity),
})
export type ActivityListResponse = z.infer<typeof ActivityListResponse>

// Add flow requires name + location. Everything else is filled in later
// from the card / detail page.
export const ActivityCreateBody = z.object({
	name: ActivityName,
	location: ActivityLocation,
})
export type ActivityCreateBody = z.infer<typeof ActivityCreateBody>

export const ActivityCreateResponse = z.object({ activity: Activity })
export type ActivityCreateResponse = z.infer<typeof ActivityCreateResponse>

// Update semantics: omit a key to leave it alone. `null` clears nullable
// fields. Arrays are replaced wholesale.
export const ActivityUpdateBody = z
	.object({
		name: ActivityName.optional(),
		// Replaces the location wholesale, town included — there is no
		// location-editing UI, so nothing sends this today.
		location: ActivityLocation.optional(),
		types: z.array(ActivityType).optional(),
		cost: Cost.nullable().optional(),
		weatherSuits: z.array(WeatherSuit).optional(),
		audience: z.array(Audience).optional(),
		accessibility: z.array(Accessibility).optional(),
		taggingStatus: ActivityTaggingStatus.optional(),
		notes: z.string().trim().max(2000).nullable().optional(),
	})
	.refine(
		(b) =>
			b.name !== undefined ||
			b.location !== undefined ||
			b.types !== undefined ||
			b.cost !== undefined ||
			b.weatherSuits !== undefined ||
			b.audience !== undefined ||
			b.accessibility !== undefined ||
			b.taggingStatus !== undefined ||
			b.notes !== undefined,
		{ message: 'At least one field must be provided' },
	)
export type ActivityUpdateBody = z.infer<typeof ActivityUpdateBody>

export const ActivityUpdateResponse = z.object({ activity: Activity })
export type ActivityUpdateResponse = z.infer<typeof ActivityUpdateResponse>

export const ActivityDeleteResponse = z.object({ ok: z.literal(true) })
export type ActivityDeleteResponse = z.infer<typeof ActivityDeleteResponse>

// "Did this today" — the server stamps the timestamp atomically via
// DDB list_append so two concurrent presses can't lose a visit. Body
// is empty.
export const ActivityVisitResponse = z.object({ activity: Activity })
export type ActivityVisitResponse = z.infer<typeof ActivityVisitResponse>

// Geocoder response — proxied via /activities/geocode so we send the
// User-Agent Nominatim's policy requires and never expose the user's IP
// directly. `displayName` is the verbose Nominatim string; the UI shows
// it in the dropdown and seeds the editable friendlyName.
export const GeocodeResultItem = z.object({
	lat: z.number().gte(-90).lte(90),
	lng: z.number().gte(-180).lte(180),
	displayName: z.string().min(1),
	// The locality the result sits in. `.default(null)` so a new client talking
	// to a server deployed before this field still parses the response.
	town: z.string().min(1).nullable().default(null),
})
export type GeocodeResultItem = z.infer<typeof GeocodeResultItem>

export const GeocodeResponse = z.object({
	results: z.array(GeocodeResultItem),
})
export type GeocodeResponse = z.infer<typeof GeocodeResponse>

// Reverse geocode — a coordinate turned into a short locality label (town /
// suburb), proxied via /activities/reverse-geocode for the same reasons as the
// forward geocode. `name` is null when Nominatim can't resolve a locality.
export const ReverseGeocodeResponse = z.object({
	name: z.string().min(1).nullable(),
})
export type ReverseGeocodeResponse = z.infer<typeof ReverseGeocodeResponse>
